/**
 * useMidPointDetection - Hook para detectar y mostrar mid-points en edges
 *
 * @version 1.0
 * @created 2026-03-06
 * @module core/spatial/hooks
 *
 * @description
 * Detecta cuando el cursor está cerca del mid-point de un edge y provee
 * información para renderizar un handle visual interactivo.
 *
 * Condiciones para mostrar mid-point:
 * - Hover sobre el edge
 * - Edge seleccionado
 * - Zoom suficiente (opcional)
 *
 * @example
 * ```tsx
 * const { midPoint, edgeId } = useMidPointDetection({
 *   enabled: true,
 *   hoveredEdgeId: 'edge-123',
 * });
 *
 * if (midPoint) {
 *   // Renderizar handle en midPoint.screenCoordinates
 * }
 * ```
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { mapEngine } from '../../map-engine/MapEngineService';
import { calculateMidPoint } from '../services/topology.service';
import type { LineStringGeometry } from '../types';

/**
 * Información del mid-point detectado
 */
export interface MidPointInfo {
  /** ID del edge */
  edgeId: string;
  /** Coordenadas geográficas [lng, lat] del mid-point */
  geoCoordinates: [number, number];
  /** Coordenadas de pantalla [x, y] para renderizar handle */
  screenCoordinates: [number, number];
  /** Geometría completa del edge */
  edgeGeometry: LineStringGeometry;
}

/**
 * Configuración del hook
 */
export interface UseMidPointDetectionConfig {
  /** Si la detección está habilitada */
  enabled: boolean;
  /** ID del edge con hover (para mostrar mid-point) */
  hoveredEdgeId: string | null;
  /** ID del edge seleccionado (para mostrar mid-point siempre) */
  selectedEdgeId?: string | null;
  /** Zoom mínimo para mostrar mid-points (opcional) */
  minZoom?: number;
  /** Distancia máxima en píxeles del cursor al mid-point para considerarlo "cerca" */
  hoverThreshold?: number;
}

/**
 * Hook para detectar mid-points en edges
 */
export function useMidPointDetection({
  enabled,
  hoveredEdgeId,
  selectedEdgeId = null,
  minZoom = 14,
  hoverThreshold = 20,
}: UseMidPointDetectionConfig): {
  /** Información del mid-point detectado (null si no hay) */
  midPoint: MidPointInfo | null;
  /** Si el cursor está cerca del mid-point */
  isNearMidPoint: boolean;
} {
  const [midPoint, setMidPoint] = useState<MidPointInfo | null>(null);
  const [isNearMidPoint, setIsNearMidPoint] = useState(false);
  const lastMousePos = useRef<{ x: number; y: number } | null>(null);

  /**
   * Calcula mid-point de un edge
   */
  const calculateMidPointForEdge = useCallback((edgeId: string): MidPointInfo | null => {
    const map = mapEngine.getMap();
    if (!map) return null;

    const source = map.getSource('canonical-network') as maplibregl.GeoJSONSource;
    if (!source) return null;

    const sourceData = (source as any)._data as GeoJSON.FeatureCollection;
    if (!sourceData || !sourceData.features) return null;

    // Buscar edge
    const edgeFeature = sourceData.features.find(
      (f) => f.id === edgeId && f.properties?.type === 'edge'
    );

    if (!edgeFeature || edgeFeature.geometry.type !== 'LineString') return null;

    const edgeGeometry = edgeFeature.geometry as LineStringGeometry;

    // Calcular mid-point geográfico
    const geoCoordinates = calculateMidPoint(edgeGeometry);

    // Convertir a coordenadas de pantalla
    const screenPoint = map.project(geoCoordinates);
    const screenCoordinates: [number, number] = [screenPoint.x, screenPoint.y];

    return {
      edgeId,
      geoCoordinates,
      screenCoordinates,
      edgeGeometry,
    };
  }, []);

  /**
   * Actualiza mid-point cuando cambia el edge con hover o seleccionado
   */
  useEffect(() => {
    if (!enabled) {
      setMidPoint(null);
      setIsNearMidPoint(false);
      return;
    }

    const map = mapEngine.getMap();
    if (!map) return;

    // Verificar zoom mínimo
    const currentZoom = map.getZoom();
    if (currentZoom < minZoom) {
      setMidPoint(null);
      setIsNearMidPoint(false);
      return;
    }

    // Determinar qué edge mostrar (prioridad: hover > selected)
    const targetEdgeId = hoveredEdgeId || selectedEdgeId;
    if (!targetEdgeId) {
      setMidPoint(null);
      setIsNearMidPoint(false);
      return;
    }

    // Calcular mid-point
    const mp = calculateMidPointForEdge(targetEdgeId);
    setMidPoint(mp);

    // Verificar si cursor está cerca del mid-point
    if (mp && lastMousePos.current) {
      const dx = lastMousePos.current.x - mp.screenCoordinates[0];
      const dy = lastMousePos.current.y - mp.screenCoordinates[1];
      const distance = Math.sqrt(dx * dx + dy * dy);
      setIsNearMidPoint(distance <= hoverThreshold);
    } else {
      setIsNearMidPoint(false);
    }
  }, [
    enabled,
    hoveredEdgeId,
    selectedEdgeId,
    minZoom,
    hoverThreshold,
    calculateMidPointForEdge,
  ]);

  /**
   * Actualizar mid-point cuando el mapa se mueve (pan/zoom)
   */
  useEffect(() => {
    if (!enabled) return;

    const map = mapEngine.getMap();
    if (!map) return;

    const handleMapMove = (): void => {
      // Recalcular mid-point si hay un edge activo
      const targetEdgeId = hoveredEdgeId || selectedEdgeId;
      if (targetEdgeId) {
        const mp = calculateMidPointForEdge(targetEdgeId);
        setMidPoint(mp);
      }
    };

    map.on('move', handleMapMove);
    map.on('zoom', handleMapMove);

    return () => {
      map.off('move', handleMapMove);
      map.off('zoom', handleMapMove);
    };
  }, [enabled, hoveredEdgeId, selectedEdgeId, calculateMidPointForEdge]);

  /**
   * Trackear posición del mouse para detectar cercanía al mid-point
   */
  useEffect(() => {
    if (!enabled) return;

    const map = mapEngine.getMap();
    if (!map) return;

    const handleMouseMove = (e: maplibregl.MapMouseEvent): void => {
      lastMousePos.current = { x: e.point.x, y: e.point.y };

      // Verificar si está cerca del mid-point
      if (midPoint) {
        const dx = e.point.x - midPoint.screenCoordinates[0];
        const dy = e.point.y - midPoint.screenCoordinates[1];
        const distance = Math.sqrt(dx * dx + dy * dy);
        setIsNearMidPoint(distance <= hoverThreshold);
      }
    };

    map.on('mousemove', handleMouseMove);

    return () => {
      map.off('mousemove', handleMouseMove);
    };
  }, [enabled, midPoint, hoverThreshold]);

  return {
    midPoint,
    isNearMidPoint,
  };
}
